/**
 * Los tres estados transversales del portal público, con la misma regla que el
 * portal interno: **sin datos NO es un error**.
 *
 * La diferencia con `components/States.tsx` es el tono y el destinatario. Acá
 * no hay usuario que pueda arreglar nada: el que mira es un periodista o un
 * hincha, así que no se le ofrece "revisá el token" ni se le muestra un código
 * HTTP. Se le dice qué pasa y, si hay algo viejo en pantalla, se le avisa que
 * es viejo en vez de borrárselo.
 */
import { useT } from '../../i18n/use-t';
import { useTicker } from './hooks';
import { useAgo } from './parts';

export interface PublicStateLike {
  loading: boolean;
  error: Error | null;
}

/** Pantalla completa de carga o de error: sólo cuando todavía no hay NADA. */
export function PublicState({
  state,
  what,
}: {
  state: PublicStateLike;
  what: string;
}): JSX.Element {
  const t = useT();
  if (state.loading) {
    return (
      <p className="pub-empty" role="status">
        {t('common.loading', { what })}
      </p>
    );
  }
  return (
    <p className="pub-empty" role="alert">
      {t('publicPortal.loadFailed')}
    </p>
  );
}

/**
 * Pie de página del dato: cuándo se actualizó y si la última vuelta falló.
 *
 * Va en cada pantalla y no en el marco general porque es una propiedad del
 * DOCUMENTO que se está mirando, no del portal: la portada se refresca cada 5
 * segundos y el archivo de resultados cada 60, y decir "actualizado hace 3 s"
 * abajo de una tabla que se refresca cada minuto sería mentir.
 */
export function Updated({
  updatedAt,
  error,
}: {
  updatedAt: number | null;
  error: Error | null;
}): JSX.Element | null {
  const t = useT();
  const ago = useAgo();
  // Vuelve a renderizar cada segundo para que el "hace N s" avance de verdad.
  useTicker(1000);

  if (updatedAt === null) return null;
  return (
    <p className="pub-note">
      {t('publicPortal.updated', { ago: ago(updatedAt) })}
      {error !== null ? ` · ${t('publicPortal.retrying')}` : ''}
    </p>
  );
}
